import React, { useState, useRef, useEffect } from "react";
import { Palette } from "lucide-react";
import { laserPointerToolIcon } from "./icons";
import { t } from "../i18n";
import type { AppClassProperties, AppState } from "../types";
import { actionChangeViewBackgroundColor } from "../actions/actionCanvas";
import type { ActionManager } from "../actions/manager";

interface TopLeftToolbarProps {
  appState: AppState;
  app: AppClassProperties;
  actionManager: ActionManager;
}

// 预设背景颜色
const BACKGROUND_COLORS = [
  { value: "transparent", label: "白色背景", color: "#ffffff", isTransparent: true },
  { value: "#000000", label: "黑色背景", color: "#000000" },
  { value: "#22c55e", label: "绿色背景", color: "#22c55e" },
  { value: "#3b82f6", label: "蓝色背景", color: "#3b82f6" },
  { value: "#f59e0b", label: "橙色背景", color: "#f59e0b" },
  { value: "#ef4444", label: "红色背景", color: "#ef4444" },
];

export const TopLeftToolbar: React.FC<TopLeftToolbarProps> = ({
  appState,
  app,
  actionManager,
}) => {
  const [showColorPalette, setShowColorPalette] = useState(false);
  const bgSectionRef = useRef<HTMLDivElement | null>(null);

  const isLaserActive = appState.activeTool.type === "laser";

  // 从localStorage读取背景色
  useEffect(() => {
    const savedBgColor = localStorage.getItem("excalidraw-background-color");
    if (savedBgColor && savedBgColor !== appState.viewBackgroundColor) {
      actionManager.executeAction(actionChangeViewBackgroundColor, "ui", {
        viewBackgroundColor: savedBgColor,
      });
    }
  }, []);

  const saveBackgroundColor = (color: string) => {
    localStorage.setItem("excalidraw-background-color", color);
  };

  const handleBackgroundChange = (color: string) => {
    const bgColor = color === "transparent" ? "#ffffff" : color;
    actionManager.executeAction(actionChangeViewBackgroundColor, "ui", {
      viewBackgroundColor: bgColor,
    });
    saveBackgroundColor(bgColor);
    setShowColorPalette(false);
  };

  const getCurrentBackgroundColor = () => {
    return appState.viewBackgroundColor || "transparent";
  };

  // 与快捷键 K 相同的原生激光笔切换逻辑
  const toggleLaser = () => {
    if (
      appState.activeTool.type === "laser" ||
      appState.activeTool.type === "laserTrail"
    ) {
      app.setActiveTool({ type: app.defaultSelectionTool });
    } else {
      app.setActiveTool({ type: "laser" });
    }
  };

  // 背景调色板：点击外部关闭
  useEffect(() => {
    if (!showColorPalette) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (bgSectionRef.current && target && !bgSectionRef.current.contains(target)) {
        setShowColorPalette(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showColorPalette]);

  return (
    <div className="top-left-toolbar">
      <div className="top-left-toolbar__section" ref={bgSectionRef}>
        <div className="top-left-toolbar__group" style={{ position: "relative" }}>
          <button
            className={`top-left-toolbar__button ${
              showColorPalette ? "top-left-toolbar__button--active" : ""
            }`}
            onClick={() => setShowColorPalette(!showColorPalette)}
            title="背景调色板"
          >
            <Palette size={16} />
          </button>

          {showColorPalette && (
            <div className="top-left-toolbar__palette">
              <div className="top-left-toolbar__palette-grid">
                {BACKGROUND_COLORS.map((bgColor) => (
                  <button
                    key={bgColor.value}
                    className={`top-left-toolbar__color-button ${
                      getCurrentBackgroundColor() === bgColor.value
                        ? "top-left-toolbar__color-button--active"
                        : ""
                    }`}
                    onClick={() => handleBackgroundChange(bgColor.value)}
                    title={bgColor.label}
                    style={{
                      backgroundColor: bgColor.isTransparent ? "#ffffff" : bgColor.color,
                      border: bgColor.isTransparent
                        ? "2px solid #e0e0e0"
                        : `2px solid ${bgColor.color}`,
                    }}
                  >
                    {bgColor.isTransparent && (
                      <div className="top-left-toolbar__transparent-pattern">
                        <svg width="16" height="16" viewBox="0 0 16 16">
                          <defs>
                            <pattern
                              id="checkerboard"
                              patternUnits="userSpaceOnUse"
                              width="8"
                              height="8"
                            >
                              <rect width="4" height="4" fill="#f0f0f0" />
                              <rect x="4" y="4" width="4" height="4" fill="#f0f0f0" />
                              <rect x="4" y="0" width="4" height="4" fill="#ffffff" />
                              <rect x="0" y="4" width="4" height="4" fill="#ffffff" />
                            </pattern>
                          </defs>
                          <rect width="16" height="16" fill="url(#checkerboard)" rx="2" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            className={`top-left-toolbar__button ${
              isLaserActive ? "top-left-toolbar__button--active" : ""
            }`}
            onClick={toggleLaser}
            title={t("toolBar.laser")}
          >
            {laserPointerToolIcon}
          </button>
        </div>
      </div>
    </div>
  );
};
